'use client';

import { CustomMenuLink } from '@/components/custom-layout/components/custom-menu';
import CustomTooltip from '@/components/custom-tooltip';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarItemProps } from '@/hooks/use-sidebar-store';
import { AllSubKeysMessageResource } from '@/i18n/messages';
import { cn } from '@/lib/utils';
import { DropdownMenuArrow } from '@radix-ui/react-dropdown-menu';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { CustomerNavBar } from '../../type';

type CustomMenuIconContentProps = {
    className?: string;
    pathname: string;
    item: CustomerNavBar;
};

type CustomMenuContentProps = {
    className?: string;
    pathname: string;
    menus?: CustomerNavBar[];
    onClickMenuItem?: (item: CustomerNavBar) => void;
};

export const MenuIconOnly = ({
    className,
    item,
    pathname,
}: CustomMenuIconContentProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const transSidebar = useTranslations('Sidebar');

    const onClickMenuItem = useCallback((item: SidebarItemProps) => {
        if (item && item.href) {
            setIsOpen(false);
        }
    }, []);

    const nodeLevel = 1;
    const hasChildren = item.children && item.children.length > 0;

    const content = item.langResource
        ? transSidebar(
              item.langResource as AllSubKeysMessageResource<'Sidebar'>
          )
        : item.langResource;

    return (
        <div
            className={cn(
                {
                    'max-w-full': nodeLevel > 1,
                },
                className
            )}
        >
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                <CustomTooltip.Root
                    tooltipTriggerProps={{ asChild: true }}
                    tooltipContentProps={{ side: 'right', align: 'center' }}
                    title={content}
                >
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant={'secondary'}
                            className={cn(
                                'delay-50 flex w-full justify-center rounded px-3',
                                item.href === pathname
                                    ? 'bg-secondary text-secondary-foreground hover:opacity-90'
                                    : 'bg-sidebar text-sidebar-foreground hover:bg-secondary hover:text-secondary-foreground'
                            )}
                        >
                            <CustomMenuLink
                                href={item.href}
                                className={cn(
                                    'flex items-center gap-4 rounded-lg'
                                    // item.className
                                )}
                            >
                                {item.icon ? (
                                    <item.icon strokeWidth={2.5} size={16} />
                                ) : (
                                    <></>
                                )}
                            </CustomMenuLink>
                        </Button>
                    </DropdownMenuTrigger>
                </CustomTooltip.Root>

                {/* render child */}
                {hasChildren && (
                    <DropdownMenuContent
                        side='right'
                        align='start'
                        className='min-w-80 overflow-y-auto bg-background text-foreground'
                    >
                        <DropdownMenuLabel className='truncate'>
                            {content}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <CustomMenuContent
                            menus={item.children}
                            pathname={pathname}
                            // onClickMenuItem={onClickMenuItem}
                        />
                        <DropdownMenuArrow className='fill-border' />
                    </DropdownMenuContent>
                )}
            </DropdownMenu>
        </div>
    );
};

const CustomMenuContent = ({
    className,
    menus,
    pathname,
    onClickMenuItem,
}: CustomMenuContentProps) => {
    const transSidebar = useTranslations('Sidebar');

    return menus?.map((x, idx) => {
        const isCurrent = x.href === pathname;
        const itemBackground = isCurrent
            ? 'bg-sidebar text-sidebar-foreground hover:bg-sidebar hover:text-sidebar-foreground hover:opacity-80'
            : 'hover:bg-secondary-foreground hover:text-secondary';

        const itemName = x.langResource
            ? transSidebar(
                  x.langResource as AllSubKeysMessageResource<'Sidebar'>
              )
            : x.langResource;

        const content = (
            <>
                {x.icon && (
                    <x.icon strokeWidth={2.5} size={16} className='min-w-4' />
                )}
                <p className='w-full truncate'>{itemName}</p>
            </>
        );

        if (x.children && x.children.length > 0) {
            return (
                <DropdownMenuSub key={idx}>
                    <DropdownMenuSubTrigger
                        className={cn(
                            'mb-1 flex cursor-pointer items-center gap-2 py-1.5',
                            className,
                            itemBackground
                        )}
                    >
                        {content}
                    </DropdownMenuSubTrigger>

                    <DropdownMenuPortal>
                        <DropdownMenuSubContent className='h-[95vh] min-w-52 overflow-y-auto'>
                            <DropdownMenuLabel className='truncate'>
                                {x.langResource}
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <CustomMenuContent
                                menus={x.children}
                                pathname={pathname}
                                onClickMenuItem={onClickMenuItem}
                            />
                        </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                </DropdownMenuSub>
            );
        }

        return (
            <DropdownMenuItem asChild key={idx}>
                <CustomMenuLink
                    href={x.href}
                    className={cn(
                        'mb-1 flex cursor-pointer items-center gap-2 py-2',
                        itemBackground,
                        className
                    )}
                    onClick={() => onClickMenuItem?.(x)}
                >
                    {content}
                </CustomMenuLink>
            </DropdownMenuItem>
        );
    });
};
